import { useState, useEffect } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import {
  isElectron,
  getAppVersion,
  onUpdateAvailable,
  onUpdateProgress,
  onUpdateDownloaded,
  onUpdateError,
  checkForUpdate,
  installUpdate,
} from '@/lib/electron'
import { Loader2, Download, RefreshCw, X } from 'lucide-react'

function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  for (let i = 0; i < 3; i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0)
    if (diff !== 0) return diff
  }
  return 0
}

type UpdateState = 'idle' | 'checking' | 'downloading' | 'ready' | 'error'

export function UpdateGate({ children }: { children: React.ReactNode }) {
  const minimumVersion = useQuery(api.config.getMinimumDesktopVersion)
  const [updateState, setUpdateState] = useState<UpdateState>('idle')
  const [downloadPercent, setDownloadPercent] = useState(0)
  const [newVersion, setNewVersion] = useState<string | null>(null)
  const [bannerDismissed, setBannerDismissed] = useState(false)

  const appVersion = isElectron() ? getAppVersion() : null
  const isOutdated =
    appVersion && minimumVersion && minimumVersion !== '0.0.0'
      ? compareVersions(appVersion, minimumVersion) < 0
      : false

  useEffect(() => {
    if (!isElectron()) return

    const cleanups: (() => void)[] = []

    const c1 = onUpdateAvailable((data) => {
      setNewVersion(data.version)
      setUpdateState('downloading')
    })
    if (c1) cleanups.push(c1)

    const c2 = onUpdateProgress((data) => {
      setDownloadPercent(data.percent)
    })
    if (c2) cleanups.push(c2)

    const c3 = onUpdateDownloaded((data) => {
      setNewVersion(data.version)
      setUpdateState('ready')
    })
    if (c3) cleanups.push(c3)

    const c4 = onUpdateError(() => {
      setUpdateState('error')
    })
    if (c4) cleanups.push(c4)

    return () => cleanups.forEach((c) => c())
  }, [])

  useEffect(() => {
    if (isOutdated && updateState === 'idle') {
      setUpdateState('checking')
      checkForUpdate()
    }
  }, [isOutdated, updateState])

  const handleInstall = () => {
    installUpdate()
  }

  const handleRetry = () => {
    setUpdateState('checking')
    checkForUpdate()
  }

  const downloadUrl =
    typeof navigator !== 'undefined' && navigator.platform?.includes('Win')
      ? '/download-for-windows'
      : '/download-for-mac'

  // Blocking modal — version is below server minimum
  if (isOutdated) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div className="max-w-md w-full mx-4 rounded-2xl bg-card border border-border p-8 text-center shadow-2xl">
          <div className="w-14 h-14 rounded-xl bg-blue-500/10 flex items-center justify-center mx-auto mb-5">
            <Download className="w-7 h-7 text-blue-500" />
          </div>

          <h2 className="text-xl font-semibold text-foreground mb-2">
            Update Required
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            A new version of Footage is available. Please update to continue
            using the app.
          </p>

          {appVersion && (
            <p className="text-xs text-muted-foreground mb-4">
              Current: v{appVersion}
              {newVersion && <> &rarr; v{newVersion}</>}
            </p>
          )}

          {updateState === 'downloading' && (
            <div className="mb-6">
              <div className="h-2 rounded-full bg-muted overflow-hidden mb-2">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${downloadPercent}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Downloading update... {downloadPercent}%
              </p>
            </div>
          )}

          {updateState === 'checking' && (
            <div className="flex items-center justify-center gap-2 mb-6 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Checking for updates...
            </div>
          )}

          {updateState === 'ready' && (
            <button
              onClick={handleInstall}
              className="w-full py-3 px-4 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition-colors"
            >
              Restart Now
            </button>
          )}

          {updateState === 'error' && (
            <div className="space-y-3">
              <button
                onClick={handleRetry}
                className="w-full py-3 px-4 rounded-lg bg-muted text-foreground font-medium hover:bg-muted/80 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
              <a
                href={downloadUrl}
                className="block text-sm text-blue-500 hover:underline"
              >
                Download manually
              </a>
            </div>
          )}

          {(updateState === 'idle' || updateState === 'checking' || updateState === 'downloading') && (
            <a
              href={downloadUrl}
              className="block mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Or download manually
            </a>
          )}
        </div>
      </div>
    )
  }

  // Blocking modal — update downloaded and ready to install
  if (isElectron() && updateState === 'ready' && newVersion) {
    return (
      <>
        {children}
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="max-w-md w-full mx-4 rounded-2xl bg-card border border-border p-8 text-center shadow-2xl">
            <div className="w-14 h-14 rounded-xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-5">
              <Download className="w-7 h-7 text-emerald-500" />
            </div>

            <h2 className="text-xl font-semibold text-foreground mb-2">
              Update Ready
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Footage v{newVersion} has been downloaded. Restart now to apply the
              update.
            </p>

            {appVersion && (
              <p className="text-xs text-muted-foreground mb-4">
                v{appVersion} &rarr; v{newVersion}
              </p>
            )}

            <button
              onClick={handleInstall}
              className="w-full py-3 px-4 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition-colors"
            >
              Restart Now
            </button>
          </div>
        </div>
      </>
    )
  }

  // Non-blocking banner — download in progress
  const showBanner =
    isElectron() &&
    !bannerDismissed &&
    updateState === 'downloading' &&
    newVersion

  return (
    <>
      {showBanner && (
        <div className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-center gap-3 py-2 px-4 bg-blue-500 text-white text-sm">
          <span>Downloading update v{newVersion}... {downloadPercent}%</span>
          <button
            onClick={() => setBannerDismissed(true)}
            className="ml-auto p-0.5 hover:bg-white/20 rounded transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {children}
    </>
  )
}
